import type { GearSlot, Item, ItemKind } from '../content/types.ts';
import { CUSTOM_CONTENT_ID_PREFIX, isCustomContentId } from './customContentId.ts';
import { GEAR_SLOTS } from '../rules/inventory.ts';
import { isStatId } from './stats.ts';
import { isDamageType } from '../rules/damage.ts';
import { isDiceNotation } from '../rules/damageDice.ts';

/**
 * The player's own Items.
 *
 * The books contain no item catalogue (RULES_CONFLICTS OPEN-008): weapons are
 * Attack Skills, and starting gear beyond the combat choice is explicitly
 * narrative and negotiated with the GM (C&C p.18, Step 10). So the sword, the
 * coat and the "one interesting or useful item" the book tells a player to
 * invent have nowhere to live unless the player can define them.
 *
 * A custom Item is an ordinary `Item`. Everything downstream - Gear Slots,
 * equipping, Effects, Background starting equipment, portability - treats it as
 * one, because to those systems it is.
 */

export const CUSTOM_ITEM_LIBRARY_SCHEMA_VERSION = 5;

export interface CustomItemLibrary {
  kind: 'custom-item-library';
  schemaVersion: number;
  items: Item[];
  createdAt: string;
  updatedAt: string;
}

export function createCustomItemLibrary(now: string): CustomItemLibrary {
  return {
    kind: 'custom-item-library',
    schemaVersion: CUSTOM_ITEM_LIBRARY_SCHEMA_VERSION,
    items: [],
    createdAt: now,
    updatedAt: now,
  };
}

export interface LibraryValidation {
  valid: boolean;
  problems: string[];
}

export const MAX_CUSTOM_ITEM_NAME_LENGTH = 60;
export const MAX_CUSTOM_ITEM_DESCRIPTION_LENGTH = 2000;
export const MAX_CUSTOM_ITEM_NOTES_LENGTH = 4000;

const ITEM_KINDS: readonly ItemKind[] = ['gear', 'potion', 'scroll', 'container', 'misc'];

/**
 * Checks a stored library before the application trusts it.
 *
 * Same bar as custom Skills and Backgrounds: not "does this resemble an Item"
 * but "can every consumer handle this". An Item reaches the Gear Slot rules,
 * the Effect resolver and the inventory validator through here and nowhere
 * else, so anything this lets past is trusted for the rest of the run.
 */
export function validateCustomItemLibrary(raw: unknown): LibraryValidation {
  const problems: string[] = [];
  const push = (m: string) => problems.push(m);

  if (typeof raw !== 'object' || raw === null) {
    return { valid: false, problems: ['The custom Item library is not an object.'] };
  }
  const lib = raw as Record<string, unknown>;

  if (lib['kind'] !== 'custom-item-library') push('kind must be "custom-item-library".');
  if (typeof lib['schemaVersion'] !== 'number') push('schemaVersion must be a number.');
  if (typeof lib['createdAt'] !== 'string') push('createdAt must be a string.');
  if (typeof lib['updatedAt'] !== 'string') push('updatedAt must be a string.');

  const items = lib['items'];
  if (!Array.isArray(items)) {
    push('items must be a list.');
    return { valid: problems.length === 0, problems };
  }

  const seen = new Set<string>();
  items.forEach((entry, i) => checkItem(entry, i, seen, push));

  return { valid: problems.length === 0, problems };
}

function checkItem(
  entry: unknown,
  i: number,
  seen: Set<string>,
  push: (message: string) => void,
): void {
  const at = `items[${i}]`;
  if (typeof entry !== 'object' || entry === null) {
    push(`${at} is not an object.`);
    return;
  }
  const item = entry as Record<string, unknown>;

  const id = item['id'];
  if (typeof id !== 'string' || id === '') {
    push(`${at} has no id.`);
  } else if (!isCustomContentId(id)) {
    push(`${at} has id "${id}", which is not in the custom namespace.`);
  } else if (id === CUSTOM_CONTENT_ID_PREFIX) {
    push(`${at} has an id that is only the prefix.`);
  } else if (seen.has(id)) {
    push(`${at} repeats the id "${id}".`);
  } else {
    seen.add(id);
  }

  if (item['goldValue'] !== undefined && (!Number.isSafeInteger(item['goldValue']) || Number(item['goldValue']) < 0)) push(`${at} goldValue must be a non-negative safe whole number.`);

  const name = item['name'];
  if (typeof name !== 'string' || name.trim() === '') {
    push(`${at} has no name.`);
  } else if (name.length > MAX_CUSTOM_ITEM_NAME_LENGTH) {
    push(`${at} name is longer than ${MAX_CUSTOM_ITEM_NAME_LENGTH} characters.`);
  }

  if (!ITEM_KINDS.includes(item['kind'] as ItemKind)) {
    push(`${at} has "${String(item['kind'])}" as its kind, which is not a kind of Item.`);
  }

  // Slot. Null is ordinary: most things a player carries are not worn, and
  // RULE DCC-INV-002 means an Item with no slot can never modify anything.
  const slot = item['slot'];
  if (slot !== null && !(GEAR_SLOTS as readonly string[]).includes(slot as string)) {
    push(`${at} has "${String(slot)}" as its Gear Slot, which is not a slot.`);
  }

  // RULE DCC-INV-004 - held versus worn only means anything in Hands/Holding.
  const handUse = item['handUse'];
  if (handUse !== undefined) {
    if (handUse !== 'held' && handUse !== 'worn') {
      push(`${at} must be held or worn, not "${String(handUse)}".`);
    } else if (slot !== 'hands') {
      push(`${at} says how it is held, but is not a Hands/Holding item.`);
    }
  }

  // RULE DCC-INV-017 - the weapon relationship. Required-and-nullable, so
  // "not a weapon" and "an older file that never said" are different states -
  // the second is what the 2 -> 3 migration turns into the first.
  //
  // Only the SHAPE is checked here, and that is the whole contract of this
  // layer. Whether the named Attack exists and is actually a held weapon needs
  // an Attack catalogue, which the domain layer does not have and must not
  // acquire by importing a global registry. That question is answered at
  // CATALOGUE COMPOSITION, which every stored Item passes through before it can
  // be equipped by anything.
  const weaponAttackId = item['weaponAttackId'];
  if (weaponAttackId !== null && typeof weaponAttackId !== 'string') {
    push(`${at} must say which Attack weapon it is, or null.`);
  } else if (typeof weaponAttackId === 'string') {
    if (weaponAttackId.trim() === '') {
      push(`${at} has an empty Attack weapon reference.`);
    } else if (slot !== 'hands') {
      push(`${at} is a weapon, so it must occupy the Hands/Holding slot.`);
    } else if (handUse !== 'held') {
      push(`${at} is a weapon, so it must be held rather than worn.`);
    }
  }

  if (item['petGear'] !== undefined && typeof item['petGear'] !== 'boolean') push(`${at} petGear must be a boolean.`);
  if (typeof item['consumable'] !== 'boolean') {
    push(`${at} must say whether it is used up.`);
  }

  // The use effect is OPTIONAL and its absence is the ordinary case. When it is
  // present it must be one of the arms this build knows: a file naming an arm
  // from a newer version must not be waved through and then silently do
  // nothing, and there is no free-form arm to escape into.
  const use = item['use'];
  if (use !== undefined) {
    if (typeof use !== 'object' || use === null) {
      push(`${at} use must be an object when present.`);
    } else if ((use as Record<string, unknown>)['kind'] !== 'restore-mana-full') {
      push(`${at} has an unknown use effect "${String((use as Record<string, unknown>)['kind'])}".`);
    } else if (item['kind'] !== 'potion' || item['consumable'] !== true) {
      // RULE DCC-POT-001 - the only arm there is comes from the Standard Mana
      // Potion (C&C p.13), and the Action cost, cadence and Potion Sickness of
      // Rules p.49 are written about potions. Without this an Item declaring
      // `consumable: false` would be DESTROYED by the consumable service, and a
      // suit of armour could inflict Potion Sickness.
      push(
        `${at} restores Mana when used, which is a Potion effect, so it must be a consumable Potion.`,
      );
    }
  }

  const power = item['power'];
  if (power !== undefined) {
    if (use !== undefined) {
      push(`${at} has both a printed use and a Power; an Item may have only one.`);
    }
    for (const problem of itemPowerProblems(power)) push(`${at} Power ${problem}`);
  }

  if (!Array.isArray(item['modifiers'])) {
    push(`${at} modifiers must be a list.`);
  } else {
    const modifiers = item['modifiers'] as unknown[];
    modifiers.forEach((modifier, m) => {
      const problem = checkModifier(modifier);
      if (problem !== null) push(`${at} modifiers[${m}] ${problem}`);
    });
    // RULE DCC-INV-002 - only Items in Gear Slots give bonuses (Rules p.48), so
    // a modifier on something that can never be equipped could never apply.
    // The draft check already refuses this; a stored file must not be a way
    // around it.
    if (slot === null && modifiers.length > 0) {
      push(`${at} has bonuses but no Gear Slot, so they could never apply.`);
    }
  }

  const description = item['description'];
  if (description !== undefined) {
    if (typeof description !== 'string') {
      push(`${at} description must be text.`);
    } else if (description.length > MAX_CUSTOM_ITEM_DESCRIPTION_LENGTH) {
      push(`${at} description is longer than ${MAX_CUSTOM_ITEM_DESCRIPTION_LENGTH} characters.`);
    }
  }

  const notes = item['notes'];
  if (notes !== undefined) {
    if (typeof notes !== 'string') {
      push(`${at} notes must be text.`);
    } else if (notes.length > MAX_CUSTOM_ITEM_NOTES_LENGTH) {
      push(`${at} notes are longer than ${MAX_CUSTOM_ITEM_NOTES_LENGTH} characters.`);
    }
  }

  const source = item['source'];
  if (typeof source !== 'object' || source === null) {
    push(`${at} has no source.`);
    return;
  }
  const src = source as Record<string, unknown>;
  if (src['book'] !== 'custom') push(`${at} must have a custom source.`);
  if (typeof src['createdAt'] !== 'string' || src['createdAt'].trim() === '') {
    push(`${at} must record when it was created.`);
  }
}

/**
 * Which Modifier shapes a custom Item may carry.
 *
 * A DELIBERATE SUBSET of what the Effect system can express. The editor offers
 * a fixed list of targets, and a stored file may not smuggle in anything wider:
 * a custom Item is not a scripting language, and a modifier nobody can produce
 * through the interface should not be trusted just because it is in the JSON.
 *
 * Exported so the editor and the validator agree by construction rather than by
 * two lists being kept in step.
 */
export const CUSTOM_ITEM_MODIFIER_TARGETS = [
  'stat',
  'skill-rank',
  'damage-resistance',
  'elemental-resistance',
  'vulnerability',
  'immunity',
  'evade',
  'move',
  'step',
  'max-mana',
  'advantage',
  'disadvantage',
] as const;

export type CustomItemModifierTarget = (typeof CUSTOM_ITEM_MODIFIER_TARGETS)[number];

function checkModifier(modifier: unknown): string | null {
  if (typeof modifier !== 'object' || modifier === null) return 'is not an object.';
  const m = modifier as Record<string, unknown>;

  const target = m['target'];
  if (typeof target !== 'object' || target === null) return 'has no target.';
  const extraModifierFields = (allowed: readonly string[]) =>
    Object.keys(m).some((key) => !allowed.includes(key));
  const t = target as Record<string, unknown>;
  const kind = t['kind'];
  if (!(CUSTOM_ITEM_MODIFIER_TARGETS as readonly string[]).includes(kind as string)) {
    return `has "${String(kind)}" as its target, which a custom Item may not set.`;
  }

  if (kind === 'stat') {
    if (!isStatId(t['stat'])) return `names "${String(t['stat'])}", which is not a Stat.`;
    if (Object.keys(t).some((key) => key !== 'kind' && key !== 'stat')) return 'has extra Stat target fields.';
  } else if (kind === 'skill-rank') {
    if (typeof t['skillId'] !== 'string' || t['skillId'].trim() === '') {
      return 'must name a Skill ID.';
    }
    if (Object.keys(t).some((key) => key !== 'kind' && key !== 'skillId')) return 'has extra Skill target fields.';
  } else if (kind === 'elemental-resistance' || kind === 'vulnerability' || kind === 'immunity') {
    if (!isDamageType(t['damageType'])) {
      return `names "${String(t['damageType'])}", which is not a Damage Type.`;
    }
    if (Object.keys(t).some((key) => key !== 'kind' && key !== 'damageType')) {
      return 'has extra mitigation target fields.';
    }
    if (m['op'] !== 'grant') return `must grant ${kind.replace('-', ' ')} rather than add an amount.`;
    if (m['amount'] !== undefined) return 'must not carry a meaningless amount.';
    if (extraModifierFields(['target', 'op'])) return 'has extra Modifier fields.';
    return null;
  } else if (kind === 'advantage' || kind === 'disadvantage') {
    const scope = t['scope'];
    if (typeof scope !== 'object' || scope === null) return 'has no scope.';
    const scopeKind = (scope as Record<string, unknown>)['kind'];
    if (scopeKind !== 'all') {
      return `has a "${String(scopeKind)}" scope; a custom Item may only apply to all Checks.`;
    }
    if (Object.keys(scope as object).length !== 1) return 'has extra fields on its scope.';
    if (Object.keys(t).some((key) => key !== 'kind' && key !== 'scope')) return 'has extra Check target fields.';
    if (m['op'] !== 'grant') return 'must grant Advantage rather than add an amount.';
    if (m['amount'] !== undefined) return 'must not carry a meaningless amount.';
    if (extraModifierFields(['target', 'op'])) return 'has extra Modifier fields.';
    return null;
  } else {
    if (Object.keys(t).some((key) => key !== 'kind')) return 'has extra target fields.';
  }

  if (m['op'] !== 'add') return 'must add a flat amount.';
  if (!Number.isInteger(m['amount'])) return 'must be a whole amount.';
  if (m['amount'] === 0) return 'has an amount of 0, which does nothing.';
  if (extraModifierFields(['target', 'op', 'amount'])) return 'has extra Modifier fields.';
  return null;
}

/** Limits on an Item Power. Generous, but finite: a definition is stored and sent to every sheet. */
export const ITEM_POWER_LIMITS = {
  name: 40,
  effects: 6,
  charges: 99,
  healSlots: 10,
  mana: 999,
  buffLabel: 60,
  buffModifiers: 8,
  rounds: 600,
  /** One round. Nothing in the rules is shorter (Rules p.17). */
  minSeconds: 10,
  /** 365 days. */
  maxSeconds: 365 * 24 * 60 * 60,
} as const;

export const ITEM_POWER_EFFECT_KINDS = ['heal-slots', 'restore-mana', 'damage', 'buff'] as const;

function wholeBetween(value: unknown, min: number, max: number): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;
}

function onlyKeys(record: Record<string, unknown>, allowed: readonly string[]): boolean {
  return Object.keys(record).every((key) => allowed.includes(key));
}

/**
 * Everything wrong with an Item Power, as sentences that follow "Power ".
 *
 * STRICT ABOUT EXTRA FIELDS, as the Modifier check is: an unknown field is how
 * a definition written for a later version, or by hand, ends up doing less than
 * it says. Exported because the room catalogue checks GM-made Items with it.
 */
export function itemPowerProblems(raw: unknown): string[] {
  const problems: string[] = [];
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return ['must be an object.'];
  const power = raw as Record<string, unknown>;
  const limits = ITEM_POWER_LIMITS;

  if (!onlyKeys(power, ['name', 'actionCost', 'consumes', 'charges', 'cooldownSeconds', 'requiresEquipped', 'effects'])) {
    problems.push('has fields this version does not understand.');
  }
  const name = power['name'];
  if (typeof name !== 'string' || name.trim() === '') problems.push('needs a name.');
  else if (name.length > limits.name) problems.push(`name is longer than ${limits.name} characters.`);

  if (power['actionCost'] !== 0 && power['actionCost'] !== 1) problems.push('must cost 0 or 1 Actions.');
  if (typeof power['consumes'] !== 'boolean') problems.push('must say whether it uses the Item up.');
  if (typeof power['requiresEquipped'] !== 'boolean') problems.push('must say whether the Item has to be worn or held.');

  const charges = power['charges'];
  if (charges !== null) {
    if (typeof charges !== 'object' || charges === undefined || Array.isArray(charges)) {
      problems.push('charges must be an object or null.');
    } else {
      const c = charges as Record<string, unknown>;
      if (!onlyKeys(c, ['max', 'recharge'])) problems.push('charges have extra fields.');
      if (!wholeBetween(c['max'], 1, limits.charges)) problems.push(`must hold 1 to ${limits.charges} charges.`);
      const recharge = c['recharge'];
      if (recharge !== null) {
        if (typeof recharge !== 'object' || recharge === undefined || Array.isArray(recharge)) {
          problems.push('recharge must be an object or null.');
        } else {
          const r = recharge as Record<string, unknown>;
          if (!onlyKeys(r, ['seconds', 'amount'])) problems.push('recharge has extra fields.');
          if (!wholeBetween(r['seconds'], limits.minSeconds, limits.maxSeconds)) {
            problems.push('must recharge over a whole number of seconds, from one round to 365 days.');
          }
          if (r['amount'] !== 'all' && !wholeBetween(r['amount'], 1, limits.charges)) {
            problems.push('must recharge a whole number of charges, or all of them.');
          } else if (typeof r['amount'] === 'number' && typeof c['max'] === 'number' && r['amount'] > c['max']) {
            problems.push('recharges more charges at a time than it can hold.');
          }
        }
      }
    }
    if (power['consumes'] === true) {
      problems.push('cannot both have charges and be used up; a consumable is spent in one use.');
    }
  }

  const cooldown = power['cooldownSeconds'];
  if (cooldown !== null && !wholeBetween(cooldown, limits.minSeconds, limits.maxSeconds)) {
    problems.push('cooldown must be a whole number of seconds, from one round to 365 days, or null.');
  }
  if (cooldown !== null && power['consumes'] === true) {
    problems.push('cannot both have a cooldown and be used up; the cooldown belongs to an object that no longer exists.');
  }

  const effects = power['effects'];
  if (!Array.isArray(effects) || effects.length < 1) {
    problems.push('must do at least one thing.');
  } else if (effects.length > limits.effects) {
    problems.push(`does more than ${limits.effects} things.`);
  } else {
    effects.forEach((entry, index) => {
      const at = `effect ${index + 1}`;
      if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
        problems.push(`${at} is not an object.`);
        return;
      }
      const effect = entry as Record<string, unknown>;
      switch (effect['kind']) {
        case 'heal-slots':
          if (!onlyKeys(effect, ['kind', 'slots'])) problems.push(`${at} has extra fields.`);
          if (!wholeBetween(effect['slots'], 1, limits.healSlots)) {
            problems.push(`${at} must heal 1 to ${limits.healSlots} Health Bar slots.`);
          }
          break;
        case 'restore-mana':
          if (!onlyKeys(effect, ['kind', 'amount'])) problems.push(`${at} has extra fields.`);
          if (effect['amount'] !== 'full' && !wholeBetween(effect['amount'], 1, limits.mana)) {
            problems.push(`${at} must restore 1 to ${limits.mana} Mana, or all of it.`);
          }
          break;
        case 'damage':
          if (!onlyKeys(effect, ['kind', 'dice', 'damageType', 'stat'])) problems.push(`${at} has extra fields.`);
          if (!isDiceNotation(effect['dice'])) problems.push(`${at} needs damage dice such as 2d6.`);
          if (!isDamageType(effect['damageType'])) {
            problems.push(`${at} names "${String(effect['damageType'])}", which is not a Damage Type.`);
          }
          if (effect['stat'] !== null && !isStatId(effect['stat'])) {
            problems.push(`${at} adds "${String(effect['stat'])}", which is not a Stat.`);
          }
          break;
        case 'buff': {
          if (!onlyKeys(effect, ['kind', 'label', 'duration', 'modifiers'])) problems.push(`${at} has extra fields.`);
          const label = effect['label'];
          if (typeof label !== 'string' || label.trim() === '') problems.push(`${at} needs a name for its Buff.`);
          else if (label.length > limits.buffLabel) problems.push(`${at} Buff name is longer than ${limits.buffLabel} characters.`);
          const duration = effect['duration'];
          if (typeof duration !== 'object' || duration === null || Array.isArray(duration)) {
            problems.push(`${at} needs a duration.`);
          } else {
            const d = duration as Record<string, unknown>;
            if (d['kind'] === 'rounds') {
              if (!onlyKeys(d, ['kind', 'rounds']) || !wholeBetween(d['rounds'], 1, limits.rounds)) {
                problems.push(`${at} must last 1 to ${limits.rounds} rounds.`);
              }
            } else if (d['kind'] === 'seconds') {
              if (!onlyKeys(d, ['kind', 'seconds']) || !wholeBetween(d['seconds'], limits.minSeconds, limits.maxSeconds)) {
                problems.push(`${at} must last a whole number of seconds, from one round to 365 days.`);
              }
            } else if (d['kind'] === 'encounter') {
              if (!onlyKeys(d, ['kind'])) problems.push(`${at} duration has extra fields.`);
            } else {
              problems.push(`${at} has an unknown duration "${String(d['kind'])}".`);
            }
          }
          const modifiers = effect['modifiers'];
          if (!Array.isArray(modifiers) || modifiers.length < 1) {
            problems.push(`${at} Buff must change something.`);
          } else if (modifiers.length > limits.buffModifiers) {
            problems.push(`${at} Buff has more than ${limits.buffModifiers} bonuses.`);
          } else {
            modifiers.forEach((modifier, m) => {
              const problem = checkModifier(modifier);
              if (problem !== null) problems.push(`${at} bonus ${m + 1} ${problem}`);
            });
          }
          break;
        }
        default:
          problems.push(`${at} has an unknown kind "${String(effect['kind'])}".`);
      }
    });
  }
  return problems;
}

type LibraryMigration = (record: Record<string, unknown>) => Record<string, unknown>;

/**
 * 1 -> 2: Advantage and Disadvantage become GRANTS.
 *
 * Version 1 stored them as `op: 'add'` with an amount, because the editor asked
 * for one. The Effect system never read that amount: Advantage and
 * Disadvantage are counted as SOURCES and cancel one for one, so +1, +5 and -3
 * all produced exactly one source.
 *
 * The migration therefore drops the amount and switches to `grant`, which
 * preserves the old mechanical behaviour precisely - one source, whatever the
 * number said. Nothing is lost, because the number never meant anything.
 */
const migrate1to2: LibraryMigration = (record) => {
  const items = Array.isArray(record['items']) ? record['items'] : [];
  return {
    ...record,
    items: items.map((entry) => {
      if (typeof entry !== 'object' || entry === null) return entry;
      const item = entry as Record<string, unknown>;
      const modifiers = Array.isArray(item['modifiers']) ? item['modifiers'] : [];
      return {
        ...item,
        modifiers: modifiers.map((mod) => {
          if (typeof mod !== 'object' || mod === null) return mod;
          const m = mod as Record<string, unknown>;
          const target = m['target'] as Record<string, unknown> | undefined;
          const kind = target?.['kind'];
          if (kind !== 'advantage' && kind !== 'disadvantage') return m;
          const { amount: _dropped, ...rest } = m;
          return { ...rest, op: 'grant' };
        }),
      };
    }),
  };
};

/**
 * 2 -> 3: every Item states whether it is a physical weapon.
 *
 * `weaponAttackId` is required-and-nullable, so absence must not quietly become
 * a third meaning. Version 2 could not carry the field, and no version 2 Item
 * was ever a declared weapon, so null is the correct answer for all of them.
 *
 * Deliberately NOT guessed from the name. C&C p.22 says a weapon shares the
 * Skill's name, and OPEN-011 records why this application must not follow that:
 * a player's "Longsword" may be a rope with a joke name, and a migration that
 * silently made it two-handed would break a legal crawler.
 */
const migrate2to3: LibraryMigration = (record) => {
  const items = Array.isArray(record['items']) ? record['items'] : [];
  return {
    ...record,
    items: items.map((entry) => {
      if (typeof entry !== 'object' || entry === null) return entry;
      const item = entry as Record<string, unknown>;
      // Canonicalised AFTER the spread, deliberately. Spreading the old record
      // last would let a hand-edited schema 2 file keep a `weaponAttackId` it
      // had no business carrying - the concept did not exist at version 2, so
      // any such value is meaningless data that would suddenly acquire
      // mechanical force because a later version gave the name a meaning. Every
      // version 2 Item becomes null, without exception.
      return { ...item, weaponAttackId: null };
    }),
  };
};

/**
 * 3 -> 4: custom Items may carry a typed `use` effect.
 *
 * EVERY VERSION 3 ITEM BECOMES INERT, without exception. The concept did not
 * exist at version 3, so a stored `use` is either hand-edited or meaningless,
 * and letting it survive would give it mechanical force because a later version
 * gave the name a meaning - the same trap migration 2 -> 3 documents for
 * `weaponAttackId`.
 *
 * NOTHING IS GUESSED FROM THE NAME. A version 3 custom Item called "Ultimate
 * Mana Potion" does not acquire `restore-mana-full`; it acquires nothing, and
 * the player authors the effect if they want it. Absence is the canonical
 * "does nothing", so the field is DELETED rather than written as null.
 */
const migrate3to4: LibraryMigration = (record) => {
  const items = Array.isArray(record['items']) ? record['items'] : [];
  return {
    ...record,
    items: items.map((entry) => {
      if (typeof entry !== 'object' || entry === null) return entry;
      const { use: _discarded, ...rest } = entry as Record<string, unknown>;
      return rest;
    }),
  };
};

/**
 * 4 -> 5: Special Rules and the wider modifier subset become authored Item data.
 *
 * Version 4 owned neither `notes` nor the new modifier target kinds. Canonicalise
 * AFTER spreading old data so hand-edited future-looking fields do not acquire
 * V5 mechanics merely because this build now recognises their spelling.
 */
const migrate4to5: LibraryMigration = (record) => {
  const items = Array.isArray(record['items']) ? record['items'] : [];
  const v5OnlyTargets = new Set([
    'skill-rank',
    'elemental-resistance',
    'vulnerability',
    'immunity',
  ]);
  return {
    ...record,
    items: items.map((entry) => {
      if (typeof entry !== 'object' || entry === null) return entry;
      const item = entry as Record<string, unknown>;
      const { notes: _discardedNotes, ...rest } = item;
      const modifiers = Array.isArray(item['modifiers']) ? item['modifiers'] : [];
      return {
        ...rest,
        modifiers: modifiers.filter((modifier) => {
          if (typeof modifier !== 'object' || modifier === null) return true;
          const target = (modifier as Record<string, unknown>)['target'];
          if (typeof target !== 'object' || target === null) return true;
          return !v5OnlyTargets.has(String((target as Record<string, unknown>)['kind']));
        }),
      };
    }),
  };
};

export const CUSTOM_ITEM_LIBRARY_MIGRATIONS: Record<number, LibraryMigration> = {
  1: migrate1to2,
  2: migrate2to3,
  3: migrate3to4,
  4: migrate4to5,
};

export function migrateCustomItemLibraryRecord(raw: unknown): Record<string, unknown> | null {
  if (typeof raw !== 'object' || raw === null) return null;
  let record = { ...(raw as Record<string, unknown>) };

  const version = record['schemaVersion'];
  if (typeof version !== 'number') return null;
  if (version > CUSTOM_ITEM_LIBRARY_SCHEMA_VERSION) return null;

  let current = version;
  while (current < CUSTOM_ITEM_LIBRARY_SCHEMA_VERSION) {
    const step = CUSTOM_ITEM_LIBRARY_MIGRATIONS[current];
    if (!step) return null;
    record = step(record);
    current += 1;
    record['schemaVersion'] = current;
  }
  return record;
}

/** Exported for the editor's slot picker. */
export type { GearSlot, ItemKind };
